/**
 * Asset View Component
 *
 * Full-screen view for viewing/managing asset details.
 * Shows asset preview on the left and tabbed content on the right.
 */

import { useEffect, useState } from 'react';
import { IAsset, ProviderRegistry, ZapworksProvider } from '../providers';
import { useAssetNameEdit, useWorkflowStatuses } from '../hooks';
import { useAssetActions } from '../context/AssetActionsContext';
import { AssetSource, MediaType } from '../types';
import { useEchoContext } from '../echo/EchoContext';
import { Client, IAnalyticsSummary, IFileVersionEntry } from '@zappar/zw-api-client';

import { ArrowBackIcon, ProcessingIcon, DownloadIcon, PenIcon, CrossIcon } from '../../../icons';
import { ZprSelect, ZprSelectOption, ZprSelectOptionGroup, ZprTabs } from '@zpr-stencil';
import { formatDate, downloadFile } from '../utils';
import getAcceptedFileTypes from '../utils';
import { useMode } from '../context/ModeContext';
import { AssetPreview } from './AssetPreview';
import { AssetInfoAccordion } from './AssetInfoAccordion';
import { AssetDetailActions } from './AssetDetailActions';
import { LabelsAccordion } from './LabelsAccordion';
import { PublicLinkTab } from './PublicLinkTab';
import { CommentsTab } from './CommentsTab';
import { InlineInput } from '../..';

export interface AssetViewProps {
  /** Zapworks API client (used for auth/env when connecting to Echo) */
  zwApiClient: Client;
  /** Provider registry containing the Zapworks provider */
  registry: ProviderRegistry;
  /** The asset being viewed */
  asset: IAsset;
  /** Currently active source */
  activeSource: AssetSource;
  /** Callback to go back to the previous view */
  onBack: () => void;
  /** Callback to handle status changes */
  onStatusChange?: (newStatus: string) => void;
  /** Callback to handle file uploads for new versions */
  onUploadNewVersion?: (asset: IAsset, file: File) => void;
  /** Callback to handle asset deletion */
  onDelete?: (assetId: string, asset: IAsset) => Promise<boolean>;
  /** Callback to handle asset restoration */
  onRestore?: (assetId: string) => Promise<void>;
  /** Accepted file types for uploads */
  acceptedFileTypes?: MediaType[];
  /** Callback to navigate to the full analytics dashboard */
  onViewAnalytics?: () => void;
  /** Callback to handle public link creation */
  onPublicLinkCreated?: () => void;
  /** Callback invoked when a public link operation fails (e.g. duplicate slug) */
  onPublicLinkError?: (message: string) => void;
  refreshKey?: number; // Optional key to force remounting the component for refreshing data
  environmentImage?: string; // Optional environment image URL for ModelViewer previews
}

export const AssetView = ({
  zwApiClient,
  asset,
  activeSource,
  onBack,
  registry,
  onUploadNewVersion,
  onStatusChange,
  onDelete,
  onRestore,
  acceptedFileTypes = [],
  onViewAnalytics,
  onPublicLinkCreated,
  onPublicLinkError,
  refreshKey,
  environmentImage,
}: AssetViewProps) => {
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [modelSrc, setModelSrc] = useState<string>('');
  const [hasLoadedCommentsTab, setHasLoadedCommentsTab] = useState(false);
  const [hasLoadedEmbedTab, setHasLoadedEmbedTab] = useState(false);
  const [embedCode, setEmbedCode] = useState<string | undefined>(undefined);
  const { premium, useNestleFeatures, permissions } = useMode();

  const handleTabsChange = (event: CustomEvent<{ id: number }>) => {
    if (event.detail.id === 1) setHasLoadedCommentsTab(true);
    if (event.detail.id === 2) setHasLoadedEmbedTab(true);
  };

  const [selectedAsset, setSelectedAsset] = useState<IAsset>(asset);

  useEffect(() => {
    let cancelled = false;
    const fetchAssetData = async () => {
      const zapworksProvider = registry.get('zapworks') as ZapworksProvider | undefined;
      const newAsset = await zapworksProvider?.getAssetById(asset.getId(), asset.getWorkspaceId() ?? '');
      if (!cancelled && newAsset) {
        setSelectedAsset(newAsset);
      }
    };
    fetchAssetData();
    return () => { cancelled = true; };
  }, [asset, refreshKey]);

  useEffect(() => {
    const loadPreview = async () => {
      const url = await selectedAsset.getPreviewUrl({ width: 500, height: 500 }, false);
      setPreviewUrl(url);
      const modelUrl = await selectedAsset.getDownloadUrl(false);
      setModelSrc(modelUrl);
    };
    loadPreview();
  }, [selectedAsset, refreshKey]);

  const handleUpload = (file: File) => {
    if (onUploadNewVersion) {
      onUploadNewVersion(selectedAsset, file);
    }
  };

  const renderOverviewTab = () => (
    <OverviewTab premium={premium} useNestleFeatures={useNestleFeatures} registry={registry} asset={selectedAsset} activeSource={activeSource} onUpload={handleUpload} acceptedFileTypes={acceptedFileTypes} onStatusChange={onStatusChange} onDelete={onDelete} onBack={onBack} onRestore={onRestore} onViewAnalytics={onViewAnalytics} />
  )

  return (
    <div className="asset-view">
      <div className="asset-view__preview">
        <header>
          <button
            className="btn btn-ghost p0"
            onClick={onBack}
            aria-label="Close asset view"
          >
            <ArrowBackIcon />
            Back
          </button>
        </header>
        <div className="asset-view__preview-container">
          <AssetPreview
            name={selectedAsset.getName()}
            previewUrl={previewUrl}
            mediaType={selectedAsset.getMediaType()}
            modelSrc={modelSrc}
            isInteractive={true}
            embedCode={embedCode}
            key={refreshKey}
            environmentImage={environmentImage}
          />
        </div>
      </div>

      <div className="asset-view__body">
        <div className="asset-view__panel-section">
          {premium ? (
            <div className="asset-view__tabs">
              <ZprTabs onTabsChange={handleTabsChange}>
                <div tab-header="Overview"><div className="asset-view__tabs-content">{renderOverviewTab()}</div></div>
                <div tab-header="Comments"><div className="asset-view__tabs-content">{hasLoadedCommentsTab ? <CommentsTab asset={selectedAsset} zwApiClient={zwApiClient} /> : null}</div></div>
                <div tab-header="Publish"><div className="asset-view__tabs-content">{hasLoadedEmbedTab ? <PublicLinkTab asset={selectedAsset} zwApiClient={zwApiClient} onEmbedCodeBuilt={setEmbedCode} havePermissionToPublish={permissions?.canPublish === true} onPublicLinkCreated={onPublicLinkCreated} onError={onPublicLinkError} /> : null}</div></div>
              </ZprTabs>
            </div>
          ) : (
            <div className="asset-view__tabs-content">
              {renderOverviewTab()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const OverviewTab = ({ asset, registry, activeSource, acceptedFileTypes, onUpload, onStatusChange, onDelete, onRestore, onBack, premium, useNestleFeatures, onViewAnalytics }: {
  asset: IAsset; registry: ProviderRegistry; activeSource: AssetSource;
  acceptedFileTypes?: MediaType[]; onUpload?: (file: File) => void; onStatusChange?: (newStatus: string) => void; onDelete?: (assetId: string, asset: IAsset) => Promise<boolean>; onRestore?: (assetId: string) => Promise<void>; onBack?: () => void; premium: boolean; useNestleFeatures: boolean; onViewAnalytics?: () => void;
}) => {
  const { zapworksProvider, workspaceId, onAddToWorkspaceAssets } = useAssetActions();
  const { statuses: workflowStatuses } = useWorkflowStatuses(zapworksProvider, workspaceId);
  const { isToolMode, permissions } = useMode();
  const [allVersions, setAllVersions] = useState<any[]>([]);
  const { postSystemComment } = useEchoContext();
  const { editedName, isLoadingName, handleNameChange, handleSaveName, isSuccess, error } = useAssetNameEdit(asset, postSystemComment);
  const metadata = asset.getMetadata();
  const assetId = asset.getId();

  // Extract metadata fields
  const [workflowStatus, setWorkflowStatus] = useState<string>(metadata.workflowStatusId as string || '');

  useEffect(() => {
    setWorkflowStatus(asset.getMetadata().workflowStatusId as string || '');
  }, [asset]);
  const canDelete = (metadata.canDelete as boolean) || false;
  const binned = (metadata.binned as boolean) || false;
  const canRestore = (metadata.canRestore as boolean) || false;

  const [serviceNowTicket, setServiceNowTicketValue] = useState<string>((metadata.serviceNowTicket as string) || '');
  const [isLoadingTicket, setIsLoadingTicket] = useState(false);
  const [ticketSuccess, setTicketSuccess] = useState(false);
  const [ticketError, setTicketError] = useState<string | null>(null);
  const { onAddToProject } = useAssetActions();

  const handleTicketChange = (newValue: string) => {
    setServiceNowTicketValue(newValue);
    setTicketSuccess(false);
    setTicketError(null);
  };

  const handleSaveTicket = async () => {
    setIsLoadingTicket(true);
    setTicketSuccess(false);
    setTicketError(null);
    try {
      await asset.setServiceNowTicket?.(serviceNowTicket || null);
      setTicketSuccess(true);
      setTimeout(() => setTicketSuccess(false), 5000);
    } catch {
      setTicketError('Failed to update ServiceNow ticket');
      setServiceNowTicketValue((metadata.serviceNowTicket as string | null) || '');
    } finally {
      setIsLoadingTicket(false);
    }
  };

  const [analyticsSummary, setAnalyticsSummary] = useState<IAnalyticsSummary | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const handleAddToWorkspaceAssets = async (_isAdded: boolean) => {
    if (onAddToWorkspaceAssets) {
      await onAddToWorkspaceAssets([asset]);
    }
  };

  const handleMoveToBin = async () => {
    if (onDelete) await onDelete(assetId, asset);
    if (onBack) onBack();
  };

  useEffect(() => {
    const loadAllVersions = async () => {
      const zapworksProvider = registry.get('zapworks') as ZapworksProvider | undefined;
      const versions = await zapworksProvider?.getFileHistory(asset.getWorkspaceId() ?? '', assetId);
      setAllVersions(versions || []);
    };
    loadAllVersions();
  }, [assetId, registry]);

  useEffect(() => {
    if (!premium || !workspaceId) return;
    setAnalyticsLoading(true);
    zapworksProvider?.getAnalyticsSummary(workspaceId, assetId)
      .then(data => setAnalyticsSummary(data))
      .catch(() => setAnalyticsSummary({ last7DaysViews: 0, totalViews: 0 }))
      .finally(() => setAnalyticsLoading(false));
  }, [assetId, workspaceId, zapworksProvider, premium]);

  const handleStatusChange = (newStatus: string) => {
    onStatusChange?.(newStatus);
    setWorkflowStatus(newStatus);
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && onUpload) {
      onUpload(file);
      // Reset the input so the same file can be selected again
      event.target.value = '';
    }
  };

  return (
    <>
      <section>
        <h4>Asset name</h4>
        <InlineInput
          name="asset-name"
          readOnly={binned}
          disabled={binned || isLoadingName}
          value={editedName}
          onChange={handleNameChange}
          onEdit={handleSaveName}
          placeholder="Enter asset name"
          ariaLabel="Asset name"
          isLoading={isLoadingName}
          isSuccess={isSuccess}
          isError={!!error}
          errorMessage={error || 'Failed to update asset name'}
          className="asset-name-input-field"
        />
        {premium && useNestleFeatures && (
          <>
            <h4>ServiceNow ticket</h4>
            <InlineInput
              name="service-now-ticket"
              readOnly={!(permissions && Object.values(permissions).every(val => val === true))}
              disabled={isLoadingTicket || !(permissions && Object.values(permissions).every(val => val === true)) }
              value={serviceNowTicket}
              onChange={handleTicketChange}
              onEdit={handleSaveTicket}
              isLoading={isLoadingTicket}
              isSuccess={ticketSuccess}
              isError={!!ticketError}
              copyable
              errorMessage={ticketError || 'Failed to update ServiceNow ticket'}
              placeholder="Asset request link (optional)"
              ariaLabel="Asset request link (optional)"
              className="asset-name-input-field"
            />
          </>
        )}
      </section>
      <section>
        {/* AssetInfoAccordion */}
        <AssetInfoAccordion
          asset={asset}
        />
        <LabelsAccordion
          asset={asset}
          isEditableAsset={!binned}
          canEditLabels={permissions?.canEditLabels === true}
          canAssignLabels={permissions?.canAssignLabels === true}
        />
      </section>
      {premium && (
        <>
          <section>
            <h4>Status</h4>
            <div className="asset-info-row">
              <label>Status</label>
              {permissions?.canChangeStatus === true ? (
                <ZprSelect
                  variant='status'
                  label={workflowStatuses.find(s => s.uid === workflowStatus)?.name || 'None'}
                  value={workflowStatus}
                  onClick={ev => ev.stopPropagation()}
                  onSelectChanged={(e) => handleStatusChange(e.detail.value)}
                >
                  <span slot='icon'><PenIcon /></span>
                  <button slot='delete' type="button" className="btn btn-ghost p0 btn-small" onClick={ev => {ev.stopPropagation(); handleStatusChange('');}}>
                    <CrossIcon width={16} height={16} />
                  </button>
                  <ZprSelectOptionGroup>
                    {workflowStatuses.map(s => (
                      <ZprSelectOption key={s.uid} value={s.uid} label={s.name} selected={s.uid === workflowStatus}>
                        <div className="flex row acenter">
                          <span className="pl16 mb0">{s.name}</span>
                        </div>
                      </ZprSelectOption>
                    ))}
                  </ZprSelectOptionGroup>
                </ZprSelect>
              ) : workflowStatuses.find(s => s.uid === workflowStatus)?.name ?(
                <span className='status-label'>{workflowStatuses.find(s => s.uid === workflowStatus)?.name}</span>
              ) : (<span>None</span>)}
            </div>
          </section>
          <section>
            <h4>Analytics</h4>
            <div className="asset-info-row">
              <label>Last 7 days</label>
              {analyticsLoading ? (
                <ProcessingIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" />
              ) : (
                <span>{analyticsSummary?.last7DaysViews ?? 0}</span>
              )}
            </div>
            <div className="asset-info-row">
              <label>Total views</label>
              {analyticsLoading ? (
                <ProcessingIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" />
              ) : (
                <span>{analyticsSummary?.totalViews ?? 0}</span>
              )}
            </div>
            {onViewAnalytics && (
              <div>
                <button
                  type="button"
                  className="btn btn-small btn-link"
                  onClick={onViewAnalytics}
                >
                  View more
                </button>
              </div>
            )}
          </section>
          <section>
            {onUpload && (
              <label className={`btn btn-small btn-secondary ${permissions?.canUploadVersion !== true ? 'disabled' : ''}`} aria-label="Upload new version of asset">
                Upload new version
                <input
                  type="file"
                  accept={acceptedFileTypes?.map(type => getAcceptedFileTypes(type)).join(',')}
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  aria-label="Select files to upload"
                  disabled={permissions?.canUploadVersion !== true}
                />
              </label>
            )}

            {allVersions.length > 0 && (
              <>
                <h4>Version history</h4>
                <ul className="version-history">
                  {allVersions.map((version, index) => (
                    <VersionHistoryItem
                      key={version.uid}
                      version={version}
                      index={index}
                    />
                  ))}
                </ul>
              </>
            )}
          </section>
        </>
      )}
      <section>
        <h4>Actions</h4>
        <AssetDetailActions
          binned={binned}
          canDelete={canDelete}
          canRestore={canRestore}
          activeSource={activeSource}
          onMoveToBin={handleMoveToBin}
          onAddToWorkspaceAssets={() => handleAddToWorkspaceAssets(false)}
          onRestore={onRestore ? () => onRestore(assetId) : undefined}
          havePermissionToDelete={permissions?.canDeleteAsset === true}
        />
      </section>
      {isToolMode && (
        <section>
          <button
            className="btn btn-small btn-primary"
            type="button"
            aria-label="Add to project"
            onClick={() => onAddToProject?.(asset)}
          >
            Add to project
          </button>
        </section>
      )}
    </>
  )
};

const VersionHistoryItem = ({ version, index }: { version: IFileVersionEntry; index: number; }) => {
  const { zapworksProvider, workspaceId } = useAssetActions();
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const asset = await zapworksProvider?.getAssetById(version.fileId, workspaceId);
      const url = await asset?.getDownloadUrl();
      if (!url) return;
      await downloadFile(url, version.filename || 'download');
    } catch (error) {
      console.error('Failed to download file:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <li>
      <div>
        <p>{version.filename}</p>
        {version.uploadedAt && (
          <span>{formatDate(new Date(version.uploadedAt))}{version.isCurrent ? ' – Current version' : ''}</span>
        )}
      </div>
      <button
        type="button"
        onClick={handleDownload}
        disabled={isDownloading}
        className="btn btn-ghost p0"
        aria-label={`Download version ${index + 1} of asset`}
      >
        <DownloadIcon width={16} height={16} />
      </button>
    </li>
  );
};

